import { Button, IconButton, Tooltip } from '@ads/components-react';
import {
  ArrowPathIcon,
  CheckIcon,
  ClockIcon,
  DocumentIcon,
  EyeIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/shared/ui/atoms';

import { useEnrollmentDocumentStatus } from '../../../hooks/useEnrollmentDocumentStatus';
import type { DocumentStatus, PersonAttachment } from '../../../types/attachment';
import type { DocumentationStatus, EnrollmentDocument } from '../../../types/enrollment';
import { DocumentationStatusBadge } from '../../atoms/DocumentationStatusBadge';
import {
  DocumentProgressRing,
  DocumentRejectedBanner,
  SectionCard,
  SectionTitle,
} from '../../molecules/DocumentationSectionParts';
import { EnrollmentDocumentViewerModal } from '../EnrollmentDocumentViewerModal';
import {
  UpdateAttachmentStatusModal,
  type UpdateAttachmentStatusSubmitData,
} from '../UpdateAttachmentStatusModal';

export interface EnrollmentDocumentationTabProps {
  readonly documents: readonly EnrollmentDocument[];
  readonly personId: number;
  readonly enrollmentId: number;
  readonly isRefetching?: boolean;
  readonly onRefetch?: () => void;
}

const DOCUMENTATION_TO_DOCUMENT_STATUS: Record<DocumentationStatus, DocumentStatus> = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  approved_temporary: 'approvedTemporary',
  not_sent: 'pending',
};

/** Constrói um {@link PersonAttachment} mínimo para alimentar o modal de status. */
function docToPersonAttachment(doc: EnrollmentDocument, personId: number): PersonAttachment {
  return {
    id: doc.attachmentId ?? 0,
    personId,
    fileName: doc.fileName,
    description: null,
    fileType: '',
    fileSize: 0,
    documentTypeName: doc.type,
    enrollmentDocumentId: doc.id,
    documentStatus: DOCUMENTATION_TO_DOCUMENT_STATUS[doc.status],
    statusName: null,
    reasonName: doc.rejectionReason,
    isPdf: false,
    createdAt: doc.uploadedAt,
    active: true,
  };
}

function buildDocumentSubtitle(
  doc: EnrollmentDocument,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (doc.status === 'not_sent') {
    return `${t('enrollmentDocumentation.awaitingUpload')} · —`;
  }

  const uploadDate = doc.uploadedAt
    ? new Date(doc.uploadedAt).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      })
    : '';
  const approvedDate = doc.approvedAt
    ? new Date(doc.approvedAt).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      })
    : uploadDate;

  const parts: string[] = [];

  if (doc.status === 'approved') {
    parts.push(
      t('enrollmentDocumentation.approvedAtBy', {
        date: approvedDate,
        by: doc.approvedBy ?? '',
      }),
    );
  } else if (doc.status === 'approved_temporary') {
    parts.push(t('enrollmentDocumentation.approvedTempAt', { date: approvedDate }));
  } else if (doc.status === 'pending') {
    parts.push(t('enrollmentDocumentation.pendingAt', { date: uploadDate }));
  } else if (doc.status === 'rejected') {
    parts.push(t('enrollmentDocumentation.rejectedAt', { date: approvedDate }));
    if (doc.rejectionReason) parts.push(doc.rejectionReason);
  }

  if (doc.fileSize) parts.push(doc.fileSize);

  return parts.join(' · ');
}

function DocumentRow({
  doc,
  isUpdating,
  onView,
  onApprove,
  onApproveTemporary,
  onReject,
}: {
  doc: EnrollmentDocument;
  isUpdating: boolean;
  onView: (doc: EnrollmentDocument) => void;
  onApprove: (doc: EnrollmentDocument) => void;
  onApproveTemporary: (doc: EnrollmentDocument) => void;
  onReject: (doc: EnrollmentDocument) => void;
}) {
  const { t } = useTranslation('personManagement');
  const subtitle = buildDocumentSubtitle(
    doc,
    t as (key: string, opts?: Record<string, unknown>) => string,
  );
  const isNotSent = doc.status === 'not_sent';

  return (
    <div className="flex items-center justify-between gap-xxs border-b-hairline border-ctx-layout-border py-xxxs last:border-b-none">
      <div className="flex items-center justify-start gap-xxs w-full">
        <DocumentIcon className="h-xxxs w-xxxs text-ctx-content-placeholder" />
        <div className="flex min-w-none flex-1 flex-col gap-quark max-w-[50%]">
          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{doc.type}</span>
          {doc.status === 'rejected' && doc.rejectionReason ? (
            <Tooltip title={doc.rejectionReason} side="bottom" align="start">
              <span className="ts-caption-xxs text-ctx-content-placeholder text-wrap line-clamp-2 cursor-default">
                {subtitle}
              </span>
            </Tooltip>
          ) : (
            <span className="ts-caption-xxs text-ctx-content-placeholder text-wrap line-clamp-2">
              {subtitle}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-quark">
        <DocumentationStatusBadge status={doc.status} />
        {isNotSent ? (
          <Button hierarchy="secondary" size="sm" leadingIcon={PlusIcon}>
            {t('enrollmentDocumentation.attach')}
          </Button>
        ) : (
          <>
            <Tooltip title={t('actions.view')}>
              <IconButton
                icon={EyeIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.view')}
                onClick={() => onView(doc)}
              />
            </Tooltip>
            <Tooltip title={t('actions.approve')}>
              <IconButton
                icon={CheckIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.approve')}
                disabled={isUpdating}
                onClick={() => onApprove(doc)}
              />
            </Tooltip>
            <Tooltip title={t('actions.approveTemporary')}>
              <IconButton
                icon={ClockIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.approveTemporary')}
                disabled={isUpdating}
                onClick={() => onApproveTemporary(doc)}
              />
            </Tooltip>
            <Tooltip title={t('actions.reject')}>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.reject')}
                disabled={isUpdating}
                className="!text-base-danger-500"
                onClick={() => onReject(doc)}
              />
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Tab de documentação da gestão de matrícula.
 * Exibe progresso, alertas de rejeição, lista de documentos e ações de status
 * (aprovar, aprovação temporária e rejeitar com motivo).
 *
 * @param documents - Lista de documentos associados à matrícula
 * @param personId - ID da pessoa dona dos anexos (necessário para atualizar status)
 * @param enrollmentId - ID da matrícula (usado para invalidar os dados após atualizar)
 */
export function EnrollmentDocumentationTab({
  documents,
  personId,
  enrollmentId,
  isRefetching = false,
  onRefetch,
}: EnrollmentDocumentationTabProps) {
  const { t } = useTranslation('personManagement');
  const [viewingDoc, setViewingDoc] = useState<EnrollmentDocument | null>(null);
  const [rejectingDoc, setRejectingDoc] = useState<EnrollmentDocument | null>(null);

  const { approveDocument, approveDocumentTemporary, rejectDocument, isPending } =
    useEnrollmentDocumentStatus(enrollmentId, personId, () => setRejectingDoc(null));

  const handleApprove = (doc: EnrollmentDocument) => {
    if (doc.id != null) approveDocument(doc.id);
  };

  const handleApproveTemporary = (doc: EnrollmentDocument) => {
    if (doc.id != null) approveDocumentTemporary(doc.id);
  };

  const handleStatusModalSubmit = (data: UpdateAttachmentStatusSubmitData) => {
    const enrollmentDocumentId = rejectingDoc?.id;
    if (enrollmentDocumentId == null) return;

    if (data.documentationStatus === 1) {
      approveDocument(enrollmentDocumentId);
    } else if (data.documentationStatus === 2 && data.documentRejectionId) {
      rejectDocument(enrollmentDocumentId, data.documentRejectionId);
    } else if (data.documentationStatus === 3) {
      approveDocumentTemporary(enrollmentDocumentId);
    }
  };

  const approvedCount = documents.filter(
    d => d.status === 'approved' || d.status === 'approved_temporary',
  ).length;
  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const rejectedCount = documents.filter(d => d.status === 'rejected').length;
  const notSentCount = documents.filter(d => d.status === 'not_sent').length;
  const totalRequired = documents.length;

  const rejectedDocs = documents.filter(d => d.status === 'rejected');

  return (
    <div className="flex flex-col gap-xxs">
      {/* Barra de resumo em largura total: anel de progresso + chips de status. */}
      <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
        <DocumentProgressRing
          approved={approvedCount}
          total={totalRequired}
          pending={pendingCount}
          rejected={rejectedCount}
          notSent={notSentCount}
        />
      </div>

      {/* Banner único consolidado com todos os documentos rejeitados. */}
      <DocumentRejectedBanner
        docs={rejectedDocs.map(doc => ({
          id: doc.id,
          docType: doc.type,
          reason: doc.rejectionReason ?? '',
        }))}
      />

      {/* Lista de documentos */}
      <SectionCard
        header={
          <SectionTitle
            icon={DocumentIcon}
            title={t('enrollmentDocumentation.sectionTitle')}
            description={t('enrollmentDocumentation.sectionDescription', {
              count: totalRequired,
            })}
            action={
              <Button
                hierarchy="tertiary"
                size="sm"
                leadingIcon={ArrowPathIcon}
                isLoading={isRefetching}
                onClick={onRefetch}
              >
                {t('enrollmentDocumentation.recalculate')}
              </Button>
            }
          />
        }
      >
        <div className="flex flex-col">
          {isRefetching ? (
            Array.from({ length: documents.length || 3 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton sem entidade
              <div
                key={i}
                className="flex items-center gap-xxs border-b-hairline border-ctx-layout-border py-xxxs last:border-b-0"
              >
                <Skeleton className="h-[2rem] w-[2rem] shrink-0 rounded-xs" />
                <div className="flex flex-1 flex-col gap-quark">
                  <Skeleton className="h-[0.875rem] w-1/3 rounded-xs" />
                  <Skeleton className="h-[0.75rem] w-1/2 rounded-xs" />
                </div>
                <Skeleton className="h-[1.5rem] w-[4rem] rounded-xs" />
              </div>
            ))
          ) : documents.length === 0 ? (
            <p className="ts-paragraph-xs text-ctx-content-placeholder">
              {t('enrollmentDetail.noDocuments')}
            </p>
          ) : (
            documents.map(doc => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                isUpdating={isPending}
                onView={setViewingDoc}
                onApprove={handleApprove}
                onApproveTemporary={handleApproveTemporary}
                onReject={setRejectingDoc}
              />
            ))
          )}
        </div>
      </SectionCard>

      <EnrollmentDocumentViewerModal
        doc={viewingDoc}
        open={viewingDoc !== null}
        onClose={() => setViewingDoc(null)}
      />

      <UpdateAttachmentStatusModal
        personId={personId}
        attachment={rejectingDoc ? docToPersonAttachment(rejectingDoc, personId) : null}
        open={rejectingDoc !== null}
        isLoading={isPending}
        initialStatus={2}
        onClose={() => setRejectingDoc(null)}
        onSubmit={handleStatusModalSubmit}
      />
    </div>
  );
}
